import type { LucideIcon } from 'lucide-react';
import {
  ArrowRight,
  BarChart3,
  Bot,
  CheckCircle2,
  Clock3,
  Coins,
  MessageSquareHeart,
  Radar,
  Sparkles,
  Target,
  Users,
  Zap,
} from 'lucide-react';
import './Home.css';

type Highlight = {
  value: string;
  label: string;
};

type Feature = {
  icon: LucideIcon;
  title: string;
  description: string;
};

type JourneyStep = {
  title: string;
  description: string;
};

type ComparisonRow = {
  criterion: string;
  m8m: string;
  powerBot: string;
};

const highlights: Highlight[] = [
  { value: '24/7', label: 'Atendimento de vendas ativo sem pausa' },
  { value: '< 2s', label: 'Primeira resposta com contexto comercial' },
  { value: '100%', label: 'Funil desenhado para conversão, não só suporte' },
];

const features: Feature[] = [
  {
    icon: Zap,
    title: 'Velocidade que não deixa lead esfriar',
    description: 'O M8M inicia conversa, qualifica intenção e conduz próximos passos em segundos.',
  },
  {
    icon: Target,
    title: 'Conversas orientadas para meta',
    description: 'Cada bloco foi pensado para gerar reunião, proposta ou fechamento, com foco em receita.',
  },
  {
    icon: BarChart3,
    title: 'Clareza total de performance',
    description: 'Você acompanha gargalos do funil, taxa de resposta e oportunidades perdidas em tempo real.',
  },
  {
    icon: Users,
    title: 'Handoff humano com contexto',
    description: 'Quando entra o time comercial, o lead já chega classificado, com histórico e objeções mapeadas.',
  },
];

const journey: JourneyStep[] = [
  {
    title: 'Captura com inteligência',
    description: 'O M8M identifica perfil, dor e urgência logo no início para priorizar oportunidades com maior potencial.',
  },
  {
    title: 'Nutrição com abordagem de vendedor',
    description: 'Respostas objetivas, oferta certa e CTA claro para levar o contato para avanço comercial real.',
  },
  {
    title: 'Conversão com previsibilidade',
    description: 'Toda interação vira dado acionável para otimizar campanha, script e produtividade do time.',
  },
];

const comparison: ComparisonRow[] = [
  {
    criterion: 'Foco principal',
    m8m: 'Arquitetura orientada para vendas e aceleração de pipeline',
    powerBot: 'Abordagem mais genérica para automação operacional',
  },
  {
    criterion: 'Personalização do funil',
    m8m: 'Alta personalização por segmento, ticket e etapa comercial',
    powerBot: 'Fluxos mais padronizados e menos adaptáveis',
  },
  {
    criterion: 'Qualificação de leads',
    m8m: 'Qualificação ativa com critérios de compra e prioridade',
    powerBot: 'Triagem mais básica antes da equipe assumir',
  },
  {
    criterion: 'Visão de performance',
    m8m: 'Métricas de conversão e oportunidades por etapa do funil',
    powerBot: 'Métricas operacionais com menor foco em receita',
  },
  {
    criterion: 'Tempo para gerar valor',
    m8m: 'Rápido: estratégia + execução para retorno comercial imediato',
    powerBot: 'Dependência maior de ajustes para atingir performance ideal',
  },
];

export function Home() {
  return (
    <div className="sales-page">
      <div className="sales-page__orb sales-page__orb--a" aria-hidden="true" />
      <div className="sales-page__orb sales-page__orb--b" aria-hidden="true" />

      <main className="sales-container">
        <header className="sales-nav m8m-reveal">
          <div className="sales-brand">
            <Bot size={20} />
            <span>M8M Sales Bot</span>
          </div>
          <a href="#contato" className="sales-link-button">
            Quero uma demo
          </a>
        </header>

        <section className="hero-grid">
          <div className="m8m-reveal">
            <p className="hero-kicker">
              <Sparkles size={15} /> Conversas que vendem, não só respondem
            </p>
            <h1 className="hero-title">M8M: o bot de vendas que transforma atendimento em receita.</h1>
            <p className="hero-subtitle">
              Enquanto o mercado segue no piloto automático, o M8M atua como pré-vendedor digital:
              qualifica, contorna objeções e acelera o fechamento com o seu time.
            </p>
            <div className="hero-actions">
              <a
                href="mailto:vendas@georanking.com.br?subject=Quero%20uma%20demo%20do%20M8M"
                className="sales-primary-button"
              >
                Agendar apresentação
                <ArrowRight size={16} />
              </a>
              <a href="#comparativo" className="sales-secondary-button">
                Ver comparativo
              </a>
            </div>
          </div>

          <div className="hero-panel m8m-reveal" style={{ animationDelay: '120ms' }}>
            <p className="hero-panel__label">Por que o M8M domina o jogo comercial</p>
            <ul className="hero-checklist">
              <li>
                <CheckCircle2 size={17} /> Roteiros de vendas com linguagem humana e persuasiva
              </li>
              <li>
                <CheckCircle2 size={17} /> Respostas instantâneas sem perder contexto do lead
              </li>
              <li>
                <CheckCircle2 size={17} /> Escalonamento para vendedor no momento certo
              </li>
              <li>
                <CheckCircle2 size={17} /> Aprendizado contínuo com base em resultado comercial
              </li>
            </ul>
          </div>
        </section>

        <section className="stats-grid">
          {highlights.map((item, index) => (
            <article
              key={item.label}
              className="stat-card m8m-reveal"
              style={{ animationDelay: `${180 + index * 80}ms` }}
            >
              <strong>{item.value}</strong>
              <p>{item.label}</p>
            </article>
          ))}
        </section>

        <section className="section-block">
          <div className="section-head m8m-reveal">
            <h2>O que faz o M8M ser o melhor para vendas</h2>
            <p>Ele foi desenhado para aumentar taxa de avanço de funil e reduzir tempo de fechamento.</p>
          </div>
          <div className="feature-grid">
            {features.map((feature, index) => (
              <article
                key={feature.title}
                className="feature-card m8m-reveal"
                style={{ animationDelay: `${100 + index * 70}ms` }}
              >
                <feature.icon size={19} />
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section-block journey">
          <div className="section-head m8m-reveal">
            <h2>Como o M8M acelera seu funil na prática</h2>
          </div>
          <div className="journey-grid">
            {journey.map((step, index) => (
              <article
                key={step.title}
                className="journey-card m8m-reveal"
                style={{ animationDelay: `${120 + index * 80}ms` }}
              >
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="comparativo" className="section-block">
          <div className="section-head m8m-reveal">
            <h2>Comparativo direto: M8M vs PowerBot da BR24</h2>
            <p>Se o objetivo for vender mais, o M8M entrega profundidade comercial em cada etapa.</p>
          </div>
          <div className="comparison-wrap m8m-reveal" style={{ animationDelay: '120ms' }}>
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Critério</th>
                  <th>M8M</th>
                  <th>PowerBot (BR24)</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row) => (
                  <tr key={row.criterion}>
                    <td>{row.criterion}</td>
                    <td>{row.m8m}</td>
                    <td>{row.powerBot}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="comparison-note">
              <Radar size={14} />
              Comparativo estratégico para apresentação comercial; ajuste o texto final conforme escopo e plano contratado.
            </p>
          </div>
        </section>

        <section className="section-block">
          <div className="roi-grid">
            <article className="roi-card m8m-reveal">
              <Clock3 size={18} />
              <h3>Menos tempo perdido</h3>
              <p>Leads sem perfil são filtrados cedo. Seu time foca no que realmente tem potencial de compra.</p>
            </article>
            <article className="roi-card m8m-reveal" style={{ animationDelay: '90ms' }}>
              <Coins size={18} />
              <h3>Mais retorno por conversa</h3>
              <p>Cada interação avança o lead com objetivo claro, aumentando produtividade e taxa de conversão.</p>
            </article>
            <article className="roi-card m8m-reveal" style={{ animationDelay: '180ms' }}>
              <MessageSquareHeart size={18} />
              <h3>Experiência que vira confiança</h3>
              <p>Atendimento rápido, personalizado e humano para reforçar valor antes mesmo do vendedor entrar.</p>
            </article>
          </div>
        </section>

        <section id="contato" className="cta-panel m8m-reveal">
          <p className="cta-kicker">Pronto para vender com consistência?</p>
          <h2>Apresente o M8M como seu diferencial comercial e saia do padrão do mercado.</h2>
          <div className="hero-actions">
            <a
              href="mailto:vendas@georanking.com.br?subject=Quero%20fechar%20com%20o%20M8M"
              className="sales-primary-button"
            >
              Falar com especialista
              <ArrowRight size={16} />
            </a>
            <a href="https://www.bitrix24.com.br/apps/" className="sales-secondary-button">
              Ver ecossistema Bitrix24
            </a>
          </div>
        </section>

        <footer className="sales-footer">
          <p>
            &copy; {new Date().getFullYear()} M8M Sales Bot. Todos os direitos reservados.
          </p>
          <div>
            <a href="/privacy">Política de Privacidade</a>
            <a href="/terms">Termos de Uso</a>
          </div>
        </footer>
      </main>
    </div>
  );
}
